import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import AndroidPreviewModal from '../components/AndroidPreviewModal';
import ReachOutSection from '../components/ReachOutSection';

// Add type declaration for ion-icon
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'ion-icon': any;
    }
  }
}

interface WaitlistForm {
  email: string;
  platform: 'iOS' | 'Android';
  country: string;
  otherCountry?: string;
}

const screenshots = [
  { src: '/images/screenshots/home.png', alt: 'ColQuest home screen' },
  { src: '/images/screenshots/activity.png', alt: 'ColQuest activity tracking screen' },
  { src: '/images/screenshots/quests.png', alt: 'ColQuest quests screen' },
  { src: '/images/screenshots/main.png', alt: 'ColQuest main screen' },
  { src: '/images/screenshots/leaderboard.png', alt: 'ColQuest leaderboard screen' },

];

const LandingPage: React.FC = () => {
  const { lang } = useParams();
  const { t, language, setLanguage } = useLanguage();
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [formData, setFormData] = useState<WaitlistForm>({
    email: '',
    platform: 'iOS',
    country: 'France',
    otherCountry: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const waitlistRef = useRef<HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Animation states for metrics
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [animatedSummits, setAnimatedSummits] = useState(0);
  const [animatedQuests, setAnimatedQuests] = useState(0);
  const [animatedCountries, setAnimatedCountries] = useState(0);
  const statsRef = useRef<HTMLElement>(null);

  // Update language context when URL language changes
  React.useEffect(() => {
    if (lang && (lang === 'en' || lang === 'fr')) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);

  // Intersection Observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsStatsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate counters when stats become visible
  useEffect(() => {
    if (isStatsVisible) {
      const duration = 2000; // 2 seconds
      const steps = 60;
      const stepDuration = duration / steps;

      // Animate summits (3784)
      let currentSummits = 0;
      const summitIncrement = 3784 / steps;
      const summitInterval = setInterval(() => {
        currentSummits += summitIncrement;
        if (currentSummits >= 3784) {
          setAnimatedSummits(3784);
          clearInterval(summitInterval);
        } else {
          setAnimatedSummits(Math.floor(currentSummits));
        }
      }, stepDuration);

      // Animate quests (545)
      let currentQuests = 0;
      const questIncrement = 545 / steps;
      const questInterval = setInterval(() => {
        currentQuests += questIncrement;
        if (currentQuests >= 545) {
          setAnimatedQuests(545);
          clearInterval(questInterval);
        } else {
          setAnimatedQuests(Math.floor(currentQuests));
        }
      }, stepDuration);

      // Animate countries (4)
      let currentCountries = 0;
      const countryIncrement = 4 / steps;
      const countryInterval = setInterval(() => {
        currentCountries += countryIncrement;
        if (currentCountries >= 4) {
          setAnimatedCountries(4);
          clearInterval(countryInterval);
        } else {
          setAnimatedCountries(Math.floor(currentCountries));
        }
      }, stepDuration);

      return () => {
        clearInterval(summitInterval);
        clearInterval(questInterval);
        clearInterval(countryInterval);
      };
    }
  }, [isStatsVisible]);

  const getTranslationString = (value: string | string[]): string => {
    return Array.isArray(value) ? value[0] : value;
  };

  const renderMarkdownText = (text: string) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const renderFeatureList = (features: string | string[]) => {
    if (!Array.isArray(features)) return null;
    return features.map((feature, index) => (
      <li key={index}>{feature}</li>
    ));
  };

  // Auto-rotation effect
  useEffect(() => {
    if (!autoRotate) return;

    const interval = setInterval(() => {
      console.log('Auto-rotating to next image');
      setActiveIndex(current => (current + 1) % screenshots.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRotate]);

  const handleScreenshotClick = (index: number) => {
    console.log('Dot clicked:', index, 'Current activeIndex:', activeIndex, 'Screenshots length:', screenshots.length);
    
    if (index < 0 || index >= screenshots.length) {
      console.log('Invalid index:', index);
      return;
    }
    
    // Stop auto-rotation and update immediately
    setAutoRotate(false);
    setActiveIndex(index);
    
    // Re-enable auto-rotation after 5 seconds of inactivity
    setTimeout(() => {
      setAutoRotate(true);
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const { error: supabaseError } = await supabase
        .from('waitlist')
        .insert([
          {
            email: formData.email,
            platform: formData.platform,
            country: formData.country === 'Other' ? formData.otherCountry : formData.country,
            source: 'website'
          }
        ]);

      if (supabaseError) {
        // If it's a unique constraint violation, treat it as success
        if (supabaseError.code === '23505') {
          setSubmitted(true);
          return;
        }
        
        console.error('Error submitting to waitlist:', supabaseError);
        setError(getTranslationString(t('landing.waitlist.error')));
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting to waitlist:', err);
      setError(getTranslationString(t('landing.waitlist.error')));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // List of supported/planned countries
  const countries = [
    'Belgium',
    'Denmark',
    'France',
    'Germany',
    'Italy',
    'Netherlands',
    'Spain',
    'Switzerland',
    'UK',
    'US',
    'Other'
  ];

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      <main>
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-content">
            <div className="hero-text">
              <h1 id="hero-title">{t('landing.hero.title') || 'Discover and Conquer New Summits'}</h1>
              <p dangerouslySetInnerHTML={{ __html: renderMarkdownText(getTranslationString(t('landing.hero.description')) || 'Join cyclists tracking their mountain conquests and completing exciting challenges. Start your climbing adventure today and become a legend of the mountains.') }} />
            </div>
            <div className="hero-carousel" aria-label="App Screenshots">
              <div className="carousel-container">
                {/* Mobile version */}
                <div className="mobile-carousel">
                  <div className="phone-frame">
                    <img 
                      src={screenshots[activeIndex].src} 
                      alt={screenshots[activeIndex].alt} 
                      className="screenshot-img"
                      draggable="false"
                    />
                  </div>
                </div>

                {/* Desktop version */}
                <div className="desktop-carousel">
                  <div className="screenshots-carousel">
                    <div className="screenshot active">
                      <div className="phone-frame">
                        <img 
                          src={screenshots[activeIndex].src} 
                          alt={screenshots[activeIndex].alt} 
                          className="screenshot-img"
                          draggable="false"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="carousel-dots" role="tablist" aria-label="Screenshot navigation">
                  {screenshots.map((_, index) => (
                    <div 
                      key={index}
                      className={`carousel-dot ${index === activeIndex ? 'active' : ''}`}
                      onClick={() => {
                        console.log('Dot clicked directly:', index);
                        handleScreenshotClick(index);
                      }}
                      role="tab"
                      aria-selected={index === activeIndex}
                      aria-label={`Screenshot ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="how-it-works" aria-labelledby="how-it-works-title">
          <h3 id="how-it-works-title">{t('landing.howItWorks.title') || 'How It Works'}</h3>
          <div className="process-steps">
            <article className="process-step">
              <div className="step-number" aria-label="Step 1">1</div>
              <div className="step-content">
                <h4>{t('landing.howItWorks.step1.title') || 'Connect & Discover'}</h4>
                <p dangerouslySetInnerHTML={{ __html: renderMarkdownText(getTranslationString(t('landing.howItWorks.step1.description')) || 'Connect your **Strava** or **Garmin** account and explore the map to find exciting summits and challenges in your area') }} />
              </div>
            </article>
            
            <article className="process-step">
              <div className="step-number" aria-label="Step 2">2</div>
              <div className="step-content">
                <h4>{t('landing.howItWorks.step2.title') || 'Climb & Conquer'}</h4>
                <p dangerouslySetInnerHTML={{ __html: renderMarkdownText(getTranslationString(t('landing.howItWorks.step2.description')) || 'Ride to the summit and let the app automatically detect your achievement. Earn points for new discoveries and difficulty') }} />
              </div>
            </article>
            
            <article className="process-step">
              <div className="step-number" aria-label="Step 3">3</div>
              <div className="step-content">
                <h4>{t('landing.howItWorks.step3.title') || 'Complete Quests & Rise'}</h4>
                <p dangerouslySetInnerHTML={{ __html: renderMarkdownText(getTranslationString(t('landing.howItWorks.step3.description')) || 'Take on regional quests, complete challenges, and climb the leaderboard to become a legend of the mountains') }} />
              </div>
            </article>
          </div>
          
          <div className="faq-link">
            <p>{t('landing.howItWorks.faqText') || 'Have questions about how it works?'}</p>
            <a href={`/${language}/faq`} className="faq-link-button">
              {t('landing.howItWorks.faqButton') || 'Check our FAQ'}
            </a>
          </div>
        </section>

        <section className="stats-section" ref={statsRef} aria-labelledby="stats-title">
          <h2 id="stats-title" className="visually-hidden">ColQuest Statistics</h2>
          <div className="stats-content">
            <div className="stats-image-column">
              <div className="image-container">
                <img src="/images/map_example.png" alt="ColQuest Map Example showing summit locations and routes" className="stats-image" />
              </div>
            </div>
            <div className="stats-metrics-column">
              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-number" aria-label={`${animatedSummits.toLocaleString()} total summits`}>
                    {language === 'fr' ? animatedSummits.toLocaleString('fr-FR') : animatedSummits.toLocaleString('en-US')}
                  </div>
                  <div className="metric-label">{t('landing.stats.summits') || 'Total Summits'}</div>
                </div>
                <div className="metric-item">
                  <div className="metric-number" aria-label={`${animatedQuests.toLocaleString()} total quests`}>
                    {language === 'fr' ? animatedQuests.toLocaleString('fr-FR') : animatedQuests.toLocaleString('en-US')}
                  </div>
                  <div className="metric-label">{t('landing.stats.quests') || 'Total Quests'}</div>
                </div>
                <div className="metric-item">
                  <div className="country-flags" aria-label="Supported countries: France, Italy, Switzerland, Spain">
                    <span className="flag" role="img" aria-label="France">🇫🇷</span>
                    <span className="flag" role="img" aria-label="Italy">🇮🇹</span>
                    <span className="flag" role="img" aria-label="Switzerland">🇨🇭</span>
                    <span className="flag" role="img" aria-label="Spain">🇪🇸</span>
                  </div>
                  <div className="metric-label">
                    {t('landing.stats.countries') || 'Supported Countries'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="submit-summit-link">
            <p>{t('landing.stats.submitSummitText') || 'Do you realize a summit is missing in ColQuest?'}</p>
            <a href={`/${language}/submit-summit`} className="submit-summit-link-button">
              {t('landing.stats.submitSummitButton') || 'Submit a summit'}
            </a>
          </div>
        </section>

        <section id="download-section" className="download-cta" aria-labelledby="download-title">
          <h3 id="download-title">{t('landing.download.title') || 'Download ColQuest'}</h3>
          <p>{t('landing.download.subtitle') || 'Start your climbing adventure today'}</p>
        <div className="cta-buttons">
          <a 
            href="https://apps.apple.com/fr/app/colquest/id6743229570" 
            className="store-badge"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download ColQuest on the App Store"
            data-umami-event="outbound-link-click"
            data-umami-event-url="https://apps.apple.com/fr/app/colquest/id6743229570"
          >
            <img 
              src="/images/app_store_download_badge.svg" 
              alt={getTranslationString(t('landing.download.appStore'))}
              className="download-badge"
            />
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=com.colquest.app" 
            className="store-badge"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download ColQuest on Google Play"
            data-umami-event="outbound-link-click"
            data-umami-event-url="https://play.google.com/store/apps/details?id=com.colquest.app"
          >
            <img 
              src="/images/play_store_download_badge.png" 
              alt={getTranslationString(t('landing.download.playStore'))}
              className="download-badge"
            />
          </a>
          </div>
        </section>

        <section className="reach-out-section" aria-labelledby="reach-out-title">
          <h3 id="reach-out-title">{t('landing.reachOut.title') || 'Reach Out'}</h3>
          <ReachOutSection />
        </section>
      </main>

      <AndroidPreviewModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default LandingPage; 